import { z } from "zod";
import {
  RECORDING_ANALYZED_MINUTES_LIMIT_CODE,
  RECORDING_INTAKE_FILE_NOT_FOUND_CODE,
  RECORDING_TEAMMATE_DEACTIVATED_CODE,
  sharedRecordingImportErrorLabels
} from "./recordingIntake.js";
const recordingImportIdSchema = z.uuid().brand();
function asRecordingImportId(id) {
  return recordingImportIdSchema.parse(id);
}
const recordingImportErrorSchema = z.object({
  code: z.string(),
  message: z.string()
});
const allRecordingImportStatuses = [
  "pending",
  "imported",
  "failed",
  "action_required",
  "skipped"
];
const recordingImportStatusSchema = z.enum(allRecordingImportStatuses);
function buildZeroRecordingImportStatusCounts() {
  return {
    pending: 0,
    imported: 0,
    failed: 0,
    action_required: 0,
    skipped: 0
  };
}
const recordingImportS3MetadataSchema = z.object({
  bucket: z.string(),
  object_key: z.string()
});
const recordingImportGenesysMetadataSchema = z.object({
  conversation_id: z.string(),
  recording_id: z.string()
});
const recordingImportZoomContactCenterMetadataSchema = z.object({
  engagement_id: z.string(),
  recording_id: z.string()
});
const RECORDING_IMPORT_TRANSIENT_ERROR_CODE = "transient_error";
const RECORDING_IMPORT_ATTEMPTS_EXHAUSTED_CODE = "attempts_exhausted";
const RECORDING_IMPORT_BUCKET_COMPANY_MISMATCH_CODE = "bucket_company_mismatch";
const RECORDING_IMPORT_TOO_LARGE_CODE = "recording_too_large";
const RECORDING_IMPORT_INVALID_METADATA_CODE = "invalid_metadata";
const RECORDING_IMPORT_MEDIA_UNAVAILABLE_CODE = "recording_media_unavailable";
const RECORDING_IMPORT_DAILY_DOWNLOAD_LIMIT_CODE = "daily_download_limit";
const RECORDING_IMPORT_UNEXPECTED_MEDIA_CONTENT_TYPE_CODE = "unexpected_media_content_type";
const RECORDING_IMPORT_ZOOM_API_ERROR_CODE = "zoom_api_error";
const recordingImportActionRequiredErrorCodes = [
  // The agent has no teammate, or the teammate matches no routing rule.
  "no_routing_for_teammate",
  RECORDING_TEAMMATE_DEACTIVATED_CODE,
  // RingCX: the agent is missing from the customer's agent email mapping (or
  // the map cache is stale, see recording-intake.md).
  "unmapped_agent_username",
  // The company's plan limit, not the pipeline: nothing on call can fix it.
  RECORDING_ANALYZED_MINUTES_LIMIT_CODE
];
const recordingImportSkippedErrorCodes = [
  // The key itself is wrong; a reprocess re-reads the same key.
  "invalid_timestamp",
  "invalid_teammate_email",
  "unknown_source",
  "missing_contact_id",
  "no_skill_no_campaign",
  // No customer to attribute the call to.
  "no_contact_number",
  "anonymous_contact_number",
  "unusable_contact_number",
  // The object cannot shrink.
  RECORDING_IMPORT_TOO_LARGE_CODE,
  // The object is gone from the bucket.
  RECORDING_INTAKE_FILE_NOT_FOUND_CODE,
  // Genesys: the customer withholds this call from the OAuth client's
  // divisions (a 403), which is their choice and nothing we can act on.
  "recording_access_forbidden",
  // Genesys: a trunk-side recording of a transfer that names none of its
  // agents; importing it would grade one rep on another's call.
  "ambiguous_transfer"
];
function resolveRecordingImportErrorDisposition(code) {
  if (recordingImportSkippedErrorCodes.includes(code)) {
    return "skipped";
  }
  if (recordingImportActionRequiredErrorCodes.includes(code)) {
    return "action_required";
  }
  return "failed";
}
const recordingImportTriageStatuses = [
  "failed",
  "action_required",
  "skipped"
];
const recordingImportCustomerStatuses = [
  "action_required",
  "failed"
];
const RECORDING_IMPORT_PAGE_SIZE = 50;
const RECORDING_IMPORT_MAX_PAGE_SIZE = 100;
function countRecordingImportCustomerRows(counts) {
  return recordingImportCustomerStatuses.reduce(
    (sum, status) => sum + counts[status],
    0
  );
}
const allRecordingImportActions = ["reprocess", "skip"];
const recordingImportActionFromStatuses = {
  reprocess: ["failed", "action_required", "skipped"],
  skip: ["failed", "action_required"]
};
function recordingImportActionsForStatus(status) {
  return allRecordingImportActions.filter(
    (action) => recordingImportActionFromStatuses[action].includes(status)
  );
}
const RECORDING_IMPORT_ACTION_MAX_IDS = 200;
const RECORDING_IMPORT_TRIAGE_ACTION_MAX_IDS = 1e3;
const RECORDING_IMPORT_ACTION_FILTER_CAP = 1e3;
const recordingImportActionSelectionSchema = z.union([
  z.object({
    ids: z.array(recordingImportIdSchema).min(1).max(RECORDING_IMPORT_ACTION_MAX_IDS)
  }),
  z.object({
    filter: z.object({
      status: recordingImportStatusSchema,
      errorCode: z.string().min(1).optional()
    })
  })
]);
const recordingImportErrorLabels = {
  skill_not_mapped: "Skill not mapped",
  campaign_not_mapped: "Campaign not mapped",
  playbook_not_found: "Playbook not found",
  teammate_not_found: "Agent not matched",
  unresolved_agent: "Agent not matched",
  unmapped_agent_username: "Agent not in agent email mapping",
  no_contact_number: "No contact number",
  anonymous_contact_number: "Caller withheld number",
  unusable_contact_number: "Unusable contact number",
  recording_too_large: "Recording too large",
  attempts_exhausted: "Import failed",
  unknown_bucket: "Unrecognized bucket",
  intake_disabled: "Intake paused by Alpharun",
  file_not_found: "Recording no longer available",
  ...sharedRecordingImportErrorLabels,
  [RECORDING_IMPORT_TRANSIENT_ERROR_CODE]: "Retrying after a temporary error",
  [RECORDING_IMPORT_BUCKET_COMPANY_MISMATCH_CODE]: "Bucket owner mismatch",
  [RECORDING_IMPORT_INVALID_METADATA_CODE]: "Ledger row malformed",
  unparsable_key: "Object key not recognized",
  invalid_timestamp: "Invalid timestamp in key",
  invalid_teammate_email: "Invalid agent email in key",
  unknown_source: "Unknown source in key",
  missing_contact_id: "Missing contact id in key",
  no_skill_no_campaign: "No skill or campaign in key",
  // Genesys. `unresolved_agent` and the contact-number codes are above.
  recording_access_forbidden: "Recording access withheld",
  ambiguous_transfer: "Transferred call, no single agent",
  recording_media_unavailable: "Recording not ready",
  conversation_not_found: "Conversation not found",
  no_agent: "No agent on the call",
  genesys_api_error: "Genesys API error",
  integration_not_configured: "Integration not configured",
  // Zoom Contact Center. `recording_media_unavailable`, `unresolved_agent`
  // and the contact-number codes are above.
  [RECORDING_IMPORT_DAILY_DOWNLOAD_LIMIT_CODE]: "Waiting for the daily download limit to reset",
  [RECORDING_IMPORT_UNEXPECTED_MEDIA_CONTENT_TYPE_CODE]: "Download was not a recording",
  [RECORDING_IMPORT_ZOOM_API_ERROR_CODE]: "Zoom API error",
  missing_recording_time: "Recording has no time"
};
function getRecordingImportErrorLabel(code) {
  if (!code) {
    return null;
  }
  return recordingImportErrorLabels[code] ?? code;
}
export {
  RECORDING_IMPORT_ACTION_FILTER_CAP,
  RECORDING_IMPORT_ACTION_MAX_IDS,
  RECORDING_IMPORT_ATTEMPTS_EXHAUSTED_CODE,
  RECORDING_IMPORT_BUCKET_COMPANY_MISMATCH_CODE,
  RECORDING_IMPORT_DAILY_DOWNLOAD_LIMIT_CODE,
  RECORDING_IMPORT_INVALID_METADATA_CODE,
  RECORDING_IMPORT_MAX_PAGE_SIZE,
  RECORDING_IMPORT_MEDIA_UNAVAILABLE_CODE,
  RECORDING_IMPORT_PAGE_SIZE,
  RECORDING_IMPORT_TOO_LARGE_CODE,
  RECORDING_IMPORT_TRANSIENT_ERROR_CODE,
  RECORDING_IMPORT_TRIAGE_ACTION_MAX_IDS,
  RECORDING_IMPORT_UNEXPECTED_MEDIA_CONTENT_TYPE_CODE,
  RECORDING_IMPORT_ZOOM_API_ERROR_CODE,
  allRecordingImportActions,
  allRecordingImportStatuses,
  asRecordingImportId,
  buildZeroRecordingImportStatusCounts,
  countRecordingImportCustomerRows,
  getRecordingImportErrorLabel,
  recordingImportActionFromStatuses,
  recordingImportActionRequiredErrorCodes,
  recordingImportActionSelectionSchema,
  recordingImportActionsForStatus,
  recordingImportCustomerStatuses,
  recordingImportErrorSchema,
  recordingImportGenesysMetadataSchema,
  recordingImportIdSchema,
  recordingImportS3MetadataSchema,
  recordingImportSkippedErrorCodes,
  recordingImportStatusSchema,
  recordingImportTriageStatuses,
  recordingImportZoomContactCenterMetadataSchema,
  resolveRecordingImportErrorDisposition
};
//# sourceMappingURL=recordingImport.js.map
